import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { type OpenCreditPackModalLocations, useTrackCreditPackModalOpened } from '@air/analytics';

import { ManageCreditPacksConfirmationModal } from '~/components/Modals/ManageCreditPacks/ManageCreditPacksConfirmationModal';
import { ManageCreditPacksPendingChangesModal } from '~/components/Modals/ManageCreditPacks/ManageCreditPacksPendingChangesModal';
import { UpdateCreditPacksModal } from '~/components/Modals/ManageCreditPacks/UpdateCreditPacksModal';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export type ManageCreditPacksModalProps = AirModalProps<{
  /** Pre-select this quantity instead of the subscription's current pack count. */
  initialSelectedQuantity?: number;
  /** Surface that opened the manage credit packs flow. Pass `undefined` explicitly when there is
   *  no attributable surface. */
  source: Exclude<OpenCreditPackModalLocations, 'checkout'> | undefined;
}>;

/**
 * Wraps `<UpdateCreditPacksModal>` with a pending-change review intercept, and routes directly
 * to `<ManageCreditPacksConfirmationModal>` when updating to 0 (credit pack cancellation).
 */
export const ManageCreditPacksModal = memo(
  ({ initialSelectedQuantity, source, onClose }: ManageCreditPacksModalProps) => {
    const { data: subscription } = useSubscriptionContext();
    const creditAnalyticsParams = useCreditAnalyticsParams();
    const [pendingChangesReviewed, setPendingChangesReviewed] = useState(false);
    const { trackCreditPackModalOpened } = useTrackCreditPackModalOpened();
    const hasTrackedOpenRef = useRef(false);

    const handleContinue = useCallback(() => setPendingChangesReviewed(true), []);

    const showInterstitial = !!subscription?.pendingPackChange && !pendingChangesReviewed;

    useEffect(() => {
      if (hasTrackedOpenRef.current || !creditAnalyticsParams) {
        return;
      }

      hasTrackedOpenRef.current = true;
      trackCreditPackModalOpened({
        source,
        ...creditAnalyticsParams,
      });
    }, [creditAnalyticsParams, source, trackCreditPackModalOpened]);

    if (showInterstitial) {
      return <ManageCreditPacksPendingChangesModal onClose={onClose} onContinue={handleContinue} />;
    }

    if (initialSelectedQuantity === 0) {
      return <ManageCreditPacksConfirmationModal source={source} targetQuantity={0} onClose={onClose} />;
    }

    return (
      <UpdateCreditPacksModal initialSelectedQuantity={initialSelectedQuantity} source={source} onClose={onClose} />
    );
  },
);

ManageCreditPacksModal.displayName = 'ManageCreditPacksModal';
